'use client';

import { cva, VariantProps } from 'class-variance-authority';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel, { EmblaViewportRefType } from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import {
    ComponentProps,
    ComponentPropsWithoutRef,
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { ComponentPropsWithRef } from 'react';
import { CategoryT, SharedCategoriesProps } from '@/entities/product/ui/categories/types';
import { cn } from '@/shared/lib/cn';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useStorageValue } from '@/shared/lib/storage';
import { Button } from '@/shared/ui/button';
import { MENU_CATEGORY_PICKER_ID } from '@/shared/ui/for/category-filter-scroll';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { CategoryContext } from '@/shared/ui/providers/category-provider';
import { categorySlideIndex } from './category-slide-index';

type CategoriesWrapperContextValue = {
    categoryId: string | null | undefined;
    subCategoryId: string | null | undefined;
    handleSelectCategory: (id: string) => void;
    handleSelectSubCategory: (id: string) => void;
};

export const CategoriesWrapperContext = createContext<CategoriesWrapperContextValue>(
    {} as CategoriesWrapperContextValue,
);

type CategoriesWrapperProps = ComponentProps<typeof Layout> & {
    storage: {
        categoryId: string;
        subCategoryId: string;
    };
};

export const CategoriesWrapper = ({ media, storage, id, ...props }: CategoriesWrapperProps) => {
    const [categoryId, setCategoryId] = useStorageValue(storage.categoryId);
    const [subCategoryId, setSubCategoryId] = useStorageValue(storage.subCategoryId);

    const handleSelectCategory = (id: string) => {
        setCategoryId(categoryId === id ? null : id);
        setSubCategoryId(null);
    };

    const handleSelectSubCategory = (id: string) => {
        setSubCategoryId(subCategoryId === id ? null : id);
    };

    return (
        <CategoriesWrapperContext.Provider
            value={{ categoryId, subCategoryId, handleSelectCategory, handleSelectSubCategory }}
        >
            <Layout
                media={{ ...media, initial: { position: 'relative', ...media?.initial } }}
                {...props}
                id={id ?? MENU_CATEGORY_PICKER_ID}
            />
        </CategoriesWrapperContext.Provider>
    );
};

const scrollIndicatorVariants = cva('categories__scroll-indicator categories__scroll-indicator--bleed', {
    variants: {
        side: {
            left: 'categories__scroll-indicator--left',
            right: 'categories__scroll-indicator--right',
        },
    },
    defaultVariants: {
        side: 'left',
    },
});

type CategoriesScrollIndicatorContextValue = {
    side: 'left' | 'right';
    handleScroll: () => void;
};

export const CategoriesScrollIndicatorContext = createContext<CategoriesScrollIndicatorContextValue>({
    side: 'left',
    handleScroll: () => {},
});

type CategoriesScrollIndicatorProps = {
    wrapperProps: ComponentPropsWithRef<typeof Layout>;
} & ComponentPropsWithRef<typeof Layout> &
    VariantProps<typeof scrollIndicatorVariants>;
export const CategoriesScrollIndicator = ({
    wrapperProps,
    className,
    ...props
}: CategoriesScrollIndicatorProps) => {
    const { side, handleScroll } = useContext(CategoriesScrollIndicatorContext);
    const t = useTranslations();

    return (
        <Layout
            className={cn(scrollIndicatorVariants({ side }), className)}
            data-testid="categories-scroll-indicator"
            data-side={side}
            aria-label={t(`CATEGORIES_SCROLL_INDICATOR.${side === 'right' ? 'RIGHT' : 'LEFT'}`)}
            {...wrapperProps}
        >
            <Layout
                element={Button}
                type="button"
                data-testid="categories-scroll-indicator-button"
                onClick={handleScroll}
                {...props}
            >
                <Icon name="arrow-left" />
            </Layout>
        </Layout>
    );
};

type CategoriesScrollIndicatorsProps = {
    disabled?: boolean;
    children?: ReactNode;
    indicator?: ReactNode;
};
export const CategoriesScrollIndicators = ({
    children,
    indicator,
    disabled = false,
}: CategoriesScrollIndicatorsProps) => {
    const { emblaApi, showLeftIndicator, showRightIndicator } = useContext(CategoriesCarouselContext);

    const handleScroll = useCallback(
        (direction: 'left' | 'right') => {
            if (!emblaApi) return;
            const selectedIndex = emblaApi.selectedScrollSnap();
            const slidesInView = emblaApi.slidesInView();
            emblaApi.scrollTo(
                selectedIndex + (direction === 'left' ? -1 : 1) * Math.round(slidesInView.length / 2),
            );
        },
        [emblaApi],
    );

    if (disabled) {
        return <>{children}</>;
    }

    return (
        <>
            {showLeftIndicator && (
                <CategoriesScrollIndicatorContext.Provider
                    value={{ side: 'left', handleScroll: () => handleScroll('left') }}
                >
                    {indicator}
                </CategoriesScrollIndicatorContext.Provider>
            )}
            {children}
            {showRightIndicator && (
                <CategoriesScrollIndicatorContext.Provider
                    value={{ side: 'right', handleScroll: () => handleScroll('right') }}
                >
                    {indicator}
                </CategoriesScrollIndicatorContext.Provider>
            )}
        </>
    );
};

export type CategoriesProps = ComponentPropsWithoutRef<'div'> &
    SharedCategoriesProps & {
        categories: CategoryT[];
        isSubcategoryVisible?: boolean;
    };

export type CategoriesCarouselContextValue = {
    emblaRef: EmblaViewportRefType | null;
    emblaApi: EmblaCarouselType | undefined;
    showLeftIndicator: boolean;
    showRightIndicator: boolean;
};

export const CategoriesCarouselContext = createContext<CategoriesCarouselContextValue>({
    emblaRef: null,
    emblaApi: undefined,
    showLeftIndicator: false,
    showRightIndicator: false,
});

type CategoriesCarouselWrapperProps = ComponentProps<typeof Layout>;

export const CategoriesCarouselWrapper = ({ children, media, ...props }: CategoriesCarouselWrapperProps) => {
    const [carouselData, setCarouselData] = useState<{ slidesInView: number[]; slidesNotInView: number[] }>({
        slidesInView: [],
        slidesNotInView: [],
    });

    const showLeftIndicator =
        typeof carouselData.slidesNotInView?.[0] === 'number' &&
        typeof carouselData.slidesInView[0] === 'number'
            ? carouselData.slidesNotInView[0] < carouselData.slidesInView[0]
            : false;
    const showRightIndicator =
        typeof carouselData.slidesNotInView?.[carouselData.slidesNotInView.length - 1] === 'number' &&
        typeof carouselData.slidesInView[carouselData.slidesInView.length - 1] === 'number'
            ? carouselData.slidesNotInView[carouselData.slidesNotInView.length - 1] >
              carouselData.slidesInView[carouselData.slidesInView.length - 1]
            : false;

    const [emblaRef, emblaApi] = useEmblaCarousel({
        active: true,
        dragFree: true,
        align: 'start',
        containScroll: 'keepSnaps',
        watchDrag: (api) => api.canScrollPrev() || api.canScrollNext(),
        inViewThreshold: 0.75,
    });

    useEffect(() => {
        if (!emblaApi) return;
        const update = (emblaApi: EmblaCarouselType) => {
            setCarouselData({
                slidesInView: emblaApi.slidesInView(),
                slidesNotInView: emblaApi.slidesNotInView(),
            });
        };
        emblaApi.on('slidesInView', update);
        update(emblaApi);
        return () => {
            emblaApi.off('slidesInView', update);
        };
    }, [emblaApi]);

    return (
        <Layout media={{ ...media, initial: { position: 'relative', ...media?.initial } }} {...props}>
            <CategoriesCarouselContext.Provider
                value={{ emblaRef, emblaApi, showLeftIndicator, showRightIndicator }}
            >
                {children}
            </CategoriesCarouselContext.Provider>
        </Layout>
    );
};

export const CategoriesCarousel = ({
    children,
    media,
    ...props
}: CategoriesProps & { media?: ComponentProps<typeof Layout>['media'] }) => {
    const { emblaRef } = useContext(CategoriesCarouselContext);
    return (
        <div ref={emblaRef} style={{ overflow: 'clip', overflowClipMargin: '1rem' }}>
            <Layout
                media={{
                    ...media,
                    initial: {
                        display: 'flex',
                        gap: 16,
                        paddingBottom: '1rem',
                        ...media?.initial,
                    },
                }}
                {...props}
            >
                {children}
            </Layout>
        </div>
    );
};

type CategoryWrapperProps = ComponentProps<typeof Layout> & { index: number };

export const CategoryWrapper = ({ media, ...props }: CategoryWrapperProps) => {
    const { data } = useContext(CategoryContext);
    const { categoryId, handleSelectCategory } = useContext(CategoriesWrapperContext);
    const { emblaApi } = useContext(CategoriesCarouselContext);

    const handleSelect = () => {
        handleSelectCategory(data.id);
        const index = emblaApi ? categorySlideIndex(emblaApi.slideNodes(), data.id) : -1;
        if (index >= 0) {
            emblaApi?.scrollTo(index);
        }
    };

    return (
        <Layout
            element="button"
            type="button"
            data-id={data.id}
            data-selected={data.id === categoryId}
            media={{
                ...media,
                initial: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'end',
                    flex: '0 0 auto',
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-foreground-dark)',
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-boxes)',
                    padding: '0.75rem 0.5rem',
                    fontWeight: 600,
                    boxShadow: 'var(--shadow-secondary)',
                    border: '1px solid var(--color-background)',
                    transition: 'border-color 0.3s, color 0.3s',
                    textWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                        borderColor: 'var(--color-primary-hover)',
                        color: 'var(--color-primary-hover)',
                    },
                    '&[data-selected="true"]': {
                        borderColor: 'var(--color-primary)',
                        color: 'var(--color-primary)',
                    },
                    ...media?.initial,
                },
            }}
            onClick={handleSelect}
            {...props}
        />
    );
};

type CategoryIconProps = ComponentProps<typeof Layout>;

export const CategoryIcon = (props: CategoryIconProps) => {
    const { data } = useContext(CategoryContext);
    const icon = getMainAsset(data.assets ?? []);
    if (!icon) {
        return null;
    }
    return (
        <Layout {...props}>
            <Image src={icon.fullUrl ?? ''} alt="" width={44} height={44} />
        </Layout>
    );
};

type CategoryNameProps = ComponentProps<typeof Layout>;

export const CategoryName = (props: CategoryNameProps) => {
    const getLocalizedValue = useGetLocalizedValue();
    const { data } = useContext(CategoryContext);
    return (
        <Layout element="span" {...props}>
            {getLocalizedValue(data.name)}
        </Layout>
    );
};

type CategoryDescriptionProps = ComponentProps<typeof Layout>;

export const CategoryDescription = (props: CategoryDescriptionProps) => {
    // Storefront snapshot omits category descriptions (slim FE payload).
    void props;
    return null;
};

type SubCategoryWrapperProps = ComponentProps<typeof Layout>;

export const SubCategoryWrapper = ({ media, ...props }: SubCategoryWrapperProps) => {
    const { data } = useContext(CategoryContext);
    const { subCategoryId, handleSelectSubCategory } = useContext(CategoriesWrapperContext);

    return (
        <Layout
            element="button"
            type="button"
            data-id={data.id}
            data-selected={data.id === subCategoryId}
            media={{
                ...media,
                initial: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-foreground-dark)',
                    borderRadius: 'var(--radius-boxes)',
                    fontWeight: 600,
                    padding: '0.25rem 1rem',
                    boxShadow: 'var(--shadow-secondary)',
                    border: '1px solid var(--color-background)',
                    transition: 'border-color 0.3s, color 0.3s',
                    '&:hover': {
                        borderColor: 'var(--color-primary-hover)',
                        color: 'var(--color-primary-hover)',
                    },
                    '&[data-selected="true"]': {
                        borderColor: 'var(--color-primary)',
                        color: 'var(--color-primary)',
                    },
                    ...media?.initial,
                },
            }}
            onClick={() => handleSelectSubCategory(data.id)}
            {...props}
        />
    );
};

type SubCategoryIconProps = ComponentProps<typeof Layout>;

export const SubCategoryIcon = (props: SubCategoryIconProps) => {
    const { data } = useContext(CategoryContext);
    const icon = getMainAsset(data.assets ?? []);
    if (!icon) {
        return null;
    }
    return (
        <Layout {...props}>
            <Image src={icon.fullUrl ?? ''} alt="" width={24} height={24} />
        </Layout>
    );
};

type SubCategoryNameProps = ComponentProps<typeof Layout>;

export const SubCategoryName = (props: SubCategoryNameProps) => {
    const getLocalizedValue = useGetLocalizedValue();
    const { data } = useContext(CategoryContext);
    return (
        <Layout element="span" {...props}>
            {getLocalizedValue(data.name)}
        </Layout>
    );
};

export {
    CategoriesWrapper as Wrapper,
    CategoriesScrollIndicator as ScrollIndicator,
    CategoriesScrollIndicators as ScrollIndicators,
    CategoriesCarouselWrapper as CarouselWrapper,
    CategoriesCarousel as Carousel,
};
